import { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Loader2, MapPin, Users, Calendar, Check, X, ArrowLeft, Trash2, Plus, Share2, Copy, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import axios from '../lib/apiTransport';
import { getUserTimezone } from '../utils/datetime';
import { useSafeBack } from '../hooks/useSafeBack';
import { APP_STORE_URL, isIPhoneWebSafari, isDesktopWeb } from '../lib/appLinks';
import { ManageMembersSheet } from '../components/ManageMembersSheet';
import PlayerFollowButton from '../components/PlayerFollowButton';
import { resolveAvatarUrl } from '../lib/avatarUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * ClubDetail - Run Club page for migration links
 * 
 * Routes:
 * - /club/:slug - Public club view with migration link flow
 * 
 * Features:
 * - Club name, location, member count
 * - Member avatar grid
 * - Upcoming runs linked to club
 * - Join Club / In Club state
 * - Preserves club context through SMS auth
 */
const ClubDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { goBack } = useSafeBack('/my-clubs');
  const { user, token, setShowAuth, setPendingClubSlug } = useContext(AuthContext);
  
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [showManageMembers, setShowManageMembers] = useState(false);
  const [pendingInvite, setPendingInvite] = useState(null);
  const [inviteAction, setInviteAction] = useState(null);

  const capabilities = club?.capabilities || {};
  const isOwner = Boolean(capabilities.can_delete_club || (user && club?.host_user_id === user.id));
  const canManageMembers = Boolean(capabilities.can_manage_members || isOwner);
  const canManageRoles = Boolean(capabilities.can_manage_roles || isOwner);
  const canCreateClubRun = Boolean(capabilities.can_create_club_run || isOwner);
  const canUseClubTools = canManageMembers || canCreateClubRun || isOwner;

  const clubRequestConfig = useCallback(() => (
    token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
  ), [token]);

  const memberRoleLabel = useCallback((member) => {
    const role = String(member?.role || 'member').toLowerCase();
    if (member?.id === club?.host_user_id || role === 'host' || role === 'owner') return 'Owner';
    if (role === 'manager' || role === 'coach') return 'Manager';
    return 'Member';
  }, [club?.host_user_id]);

  const memberRoleClass = useCallback((member) => {
    const label = memberRoleLabel(member);
    if (label === 'Owner') return 'bg-orange-500 text-white';
    if (label === 'Manager') return 'bg-blue-100 text-blue-700';
    return 'bg-zinc-100 text-zinc-600';
  }, [memberRoleLabel]);

  const handleMemberProfileClick = useCallback((member) => {
    if (!member?.username) return;
    const profilePath = `/player/${member.username}`;
    if (token) {
      navigate(profilePath);
      return;
    }
    navigate('/', {
      state: {
        showAuth: true,
        returnTo: profilePath,
      },
    });
  }, [navigate, token]);

  const handleMemberFollowChange = useCallback((memberId, newState) => {
    setClub((prev) => {
      if (!prev || !Array.isArray(prev.members)) return prev;
      return {
        ...prev,
        members: prev.members.map((member) => {
          if (member.id !== memberId) return member;
          return {
            ...member,
            follow_state: newState.following
              ? 'following'
              : newState.requested
                ? 'requested'
                : 'none',
            can_follow: true,
          };
        }),
      };
    });
  }, []);

  // Re-fetch club data after a membership change (reuses the public slug read).
  const refreshClub = useCallback(async () => {
    if (!slug) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/clubs/slug/${slug}`, clubRequestConfig());
      setClub(res.data);
    } catch (err) {
      console.error('Failed to refresh club:', err);
    }
  }, [slug, clubRequestConfig]);

  // Handle deleting club (host only)
  const handleDeleteClub = async () => {
    if (!token || !club?.id) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/clubs/${club.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`"${club.name}" has been deleted`);
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete group');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };
  
  // Handle leaving club (members only, not host)
  const handleLeaveClub = async () => {
    if (!token || !club?.id) return;
    setLeaving(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/clubs/${club.id}/leave`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`You left "${club.name}"`);
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to leave group');
    } finally {
      setLeaving(false);
      setShowLeaveConfirm(false);
    }
  };
  
  // Handle joining club
  const handleJoinClub = useCallback(async () => {
    if (!token) {
      // Save club slug for post-auth return only. Joining still requires an
      // explicit click after auth.
      localStorage.setItem('pendingClubSlug', slug);
      if (setPendingClubSlug) {
        setPendingClubSlug(slug);
      }
      setShowAuth(true);
      return;
    }
    
    setJoining(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/clubs/join-by-slug/${slug}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.joined) {
        setIsMember(true);
        setPendingInvite(null);
        toast.success(`Welcome to ${club?.name}!`);
        // Refresh club data to get updated member count
        const clubRes = await axios.get(`${BACKEND_URL}/api/clubs/slug/${slug}`, clubRequestConfig());
        setClub(clubRes.data);
      } else {
        setIsMember(true);
        toast.info('You\'re already a member');
      }
    } catch (err) {
      console.error('Failed to join club:', err);
      console.error('Failed to join club:', err);
      toast.error(err.response?.data?.detail || 'Failed to join club');
    } finally {
      setJoining(false);
    }
  }, [token, slug, club?.name, setPendingClubSlug, setShowAuth, clubRequestConfig]);

  const handleInviteAction = useCallback(async (action) => {
    if (!token || !pendingInvite?.id) return;
    setInviteAction(action);
    try {
      await axios.post(
        `${BACKEND_URL}/api/club-invites/${pendingInvite.id}/${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (action === 'accept') {
        setIsMember(true);
        setPendingInvite(null);
        toast.success('Club invite accepted');
        await refreshClub();
      } else {
        setPendingInvite(null);
        toast.success('Club invite declined');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || `Could not ${action} invite`);
    } finally {
      setInviteAction(null);
    }
  }, [token, pendingInvite?.id, refreshClub]);
  
  // Fetch club data
  useEffect(() => {
    const fetchClub = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const res = await axios.get(`${BACKEND_URL}/api/clubs/slug/${slug}`, clubRequestConfig());
        setClub(res.data);
        
        // Check membership if logged in
        if (token && res.data?.id) {
          try {
            const memberRes = await axios.get(
              `${BACKEND_URL}/api/clubs/${res.data.id}/membership`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setIsMember(memberRes.data.is_member);
          } catch {
            // Membership check failed, assume not a member
          }
          try {
            const inviteRes = await axios.get(
              `${BACKEND_URL}/api/clubs/${res.data.id}/my-pending-invite`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setPendingInvite(inviteRes.data?.invite || null);
          } catch {
            setPendingInvite(null);
          }
        } else {
          setIsMember(false);
          setPendingInvite(null);
        }
      } catch (err) {
        console.error('Failed to fetch club:', err);
        console.error('Failed to fetch club:', err);
        setError(err.response?.data?.detail || 'Club not found');
      } finally {
        setLoading(false);
      }
    };
    
    if (slug) {
      fetchClub();
    }
  }, [slug, token, clubRequestConfig]);
  
  // Format date for run cards
  const formatRunDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const userTz = getUserTimezone();
      const dayAbbr = date.toLocaleDateString('en-US', { weekday: 'short', timeZone: userTz });
      const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: userTz });
      const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: userTz });
      return `${dayAbbr}, ${monthDay} · ${time}`;
    } catch {
      return '';
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="text-6xl mb-4">🏀</div>
          <h1 className="text-xl font-bold text-zinc-900 mb-2">Club Not Found</h1>
          <p className="text-zinc-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <div className="bg-zinc-900 text-white">
        <div className="max-w-2xl mx-auto px-4 py-8">
          {/* Back button */}
          <button 
            onClick={goBack}
            className="flex items-center text-zinc-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </button>
          
          {/* Club Info */}
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-2" data-testid="club-name">
              {club?.name}
            </h1>
            <div className="flex items-center justify-center gap-2 text-zinc-400 mb-4">
              <MapPin className="w-4 h-4" />
              <span data-testid="club-location">{club?.location_label}</span>
            </div>
            
            {/* Member Count */}
            <div className="flex items-center justify-center gap-2 text-zinc-300 mb-6">
              <Users className="w-4 h-4" />
              <span data-testid="club-member-count">{club?.member_count} members</span>
            </div>
            
            {/* Host Badge */}
            {club?.host && (
              <button
                type="button"
                onClick={() => club.host.id && navigate(`/host/${club.host.id}`)}
                className="mx-auto flex items-center justify-center gap-2 text-sm text-zinc-400 mb-6 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
              >
                <span>Hosted by</span>
                <Avatar className="w-6 h-6">
                  <AvatarImage src={resolveAvatarUrl(club.host.profile_image_url)} />
                  <AvatarFallback className="bg-zinc-700 text-xs">
                    {(club.host.display_name || club.host.username || '?').charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="font-medium text-white">{club.host.display_name || club.host.username}</span>
              </button>
            )}

            {/* Hostless Club Notice — shown when host's account was deleted but club has other members */}
            {club && !club.host && club.host_user_id === null && (
              <div
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1 mb-6 text-xs text-zinc-400 bg-zinc-800/60 rounded-full border border-zinc-700"
                data-testid="club-looking-for-host"
              >
                <span>Looking for a new host</span>
              </div>
            )}
            
            {/* Join Button */}
            {isMember ? (
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-600/20 text-green-400 rounded-full">
                <Check className="w-5 h-5" />
                <span className="font-medium">You're In</span>
              </div>
            ) : pendingInvite ? (
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                  onClick={() => handleInviteAction('accept')}
                  disabled={Boolean(inviteAction)}
                  size="lg"
                  className="bg-orange-500 hover:bg-orange-600 text-white px-8"
                  data-testid="accept-club-invite-btn"
                >
                  {inviteAction === 'accept' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  Accept Invite
                </Button>
                <Button
                  onClick={() => handleInviteAction('decline')}
                  disabled={Boolean(inviteAction)}
                  size="lg"
                  variant="outline"
                  className="border-zinc-600 bg-transparent text-white hover:bg-white/10 hover:text-white px-8"
                  data-testid="decline-club-invite-btn"
                >
                  {inviteAction === 'decline' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <X className="w-4 h-4 mr-2" />
                  )}
                  Decline
                </Button>
              </div>
            ) : (
              <Button
                onClick={handleJoinClub}
                disabled={joining}
                size="lg"
                className="bg-orange-500 hover:bg-orange-600 text-white px-8"
                data-testid="join-club-btn"
              >
                {joining ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Joining...
                  </>
                ) : (
                  'Join Club'
                )}
              </Button>
            )}

            {/* iOS install-conversion layer (web only). Never blocks join.
                - iPhone Safari: secondary App Store CTA.
                - Desktop: soft "open on your phone" hint.
                - Android / Capacitor native / iPad / in-app iOS browsers:
                  render nothing (no broken Play Store, no redundant native
                  prompt). */}
            {isIPhoneWebSafari() && (
              <div className="mt-4 flex justify-center">
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/15 text-white text-sm font-medium transition-colors"
                  data-testid="ios-app-cta"
                >
                  <Smartphone className="w-4 h-4" />
                  Get the iOS app
                </a>
              </div>
            )}
            {isDesktopWeb() && (
              <p
                className="mt-4 text-xs text-zinc-400"
                data-testid="desktop-phone-hint"
              >
                For the best experience, open this link on your phone.
              </p>
            )}
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Host action bar — visible only to the club host. Provides the common
            host tasks: create a run, manage club members (add/remove adults), and
            share the public club link. */}
        {canUseClubTools && club && (
          <div className="mb-8 space-y-3" data-testid="club-host-actions">
            <div className="grid grid-cols-2 gap-3">
              {canCreateClubRun && (
              <button
                type="button"
                onClick={() => navigate(`/create-game?run_club_id=${encodeURIComponent(club.id)}`)}
                className="flex items-center justify-center gap-2 h-12 px-4 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-colors active:scale-[0.98]"
                data-testid="host-create-run-btn"
              >
                <Plus className="w-4 h-4" />
                Create Run
              </button>
              )}
              {canManageMembers && (
              <button
                type="button"
                onClick={() => setShowManageMembers(true)}
                className="flex items-center justify-center gap-2 h-12 px-4 rounded-xl bg-white border border-zinc-200 hover:bg-zinc-50 text-zinc-900 text-sm font-semibold transition-colors active:scale-[0.98]"
                data-testid="manage-members-btn"
              >
                <Users className="w-4 h-4" />
                Manage Members
              </button>
              )}
            </div>
            {isOwner && (
            <button
              type="button"
              onClick={async () => {
                const joinUrl = `https://openrunsusa.com/club/${club.slug}`;
                const shareText = `You're invited to ${club.name} — pickup basketball runs at ${club.location_label}.\n\nJoin here:\n${joinUrl}`;
                try {
                  if (navigator.share) {
                    await navigator.share({
                      title: `Join ${club.name} on Open Runs`,
                      text: shareText,
                      url: joinUrl,
                    });
                  } else {
                    await navigator.clipboard.writeText(shareText);
                    toast.success('Club link copied');
                  }
                } catch (err) {
                  // User cancelled share, or clipboard blocked — fallback toast.
                  try {
                    await navigator.clipboard.writeText(shareText);
                    toast.success('Club link copied');
                  } catch {
                    toast.error('Could not copy link');
                  }
                }
              }}
              className="w-full flex items-center justify-center gap-2 h-12 px-4 rounded-xl bg-white border border-zinc-200 hover:bg-zinc-50 text-zinc-900 text-sm font-semibold transition-colors active:scale-[0.98]"
              data-testid="share-club-link-btn"
            >
              <Share2 className="w-4 h-4" />
              Share Club Link
            </button>
            )}
          </div>
        )}

        {/* Member List — proper rows with avatar, display name, role badge,
            and join date. Host appears first (server-sorted). */}
        {club?.members && club.members.length > 0 && (
          <div className="mb-8">
            <h2 className="text-sm font-semibold text-zinc-500 uppercase tracking-wide mb-4">
              Members
            </h2>
            <ul className="space-y-2" data-testid="member-list">
              {club.members.map((member) => {
                const joined = member.joined_at ? new Date(member.joined_at) : null;
                const joinedLabel = joined && !Number.isNaN(joined.getTime())
                  ? joined.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                  : null;
                const followState = member.follow_state;
                const showFollowAction = token && followState && followState !== 'self';
                const canUseFollowButton = showFollowAction && member.can_follow !== false && followState !== 'blocked';
                return (
                  <li
                    key={member.id}
                    data-testid={`member-row-${member.id}`}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white border border-zinc-200"
                  >
                    <button
                      type="button"
                      onClick={() => handleMemberProfileClick(member)}
                      disabled={!member.username}
                      className="min-w-0 flex-1 flex items-center gap-3 rounded-lg text-left transition-colors hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 disabled:cursor-default disabled:hover:bg-transparent"
                      data-testid={`member-profile-link-${member.id}`}
                      aria-label={`View ${member.display_name || member.username || 'member'} profile`}
                    >
                      <Avatar className="w-10 h-10 shrink-0">
                        <AvatarImage src={resolveAvatarUrl(member.profile_image_url)} />
                        <AvatarFallback className="bg-orange-100 text-orange-600 font-semibold">
                          {(member.display_name || member.username || '?').charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-zinc-900 truncate" data-testid={`member-name-${member.id}`}>
                            {member.display_name || member.username}
                          </span>
                          <span
                            className={
                              'text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ' +
                              memberRoleClass(member)
                            }
                            data-testid={`member-role-${member.id}`}
                          >
                            {memberRoleLabel(member)}
                          </span>
                        </div>
                        {joinedLabel && (
                          <p className="text-xs text-zinc-500 mt-0.5">Joined {joinedLabel}</p>
                        )}
                      </div>
                    </button>
                    {followState === 'self' && (
                      <span
                        className="shrink-0 rounded-full bg-zinc-100 px-2.5 py-1 text-xs font-medium text-zinc-500"
                        data-testid={`member-self-${member.id}`}
                      >
                        You
                      </span>
                    )}
                    {showFollowAction && !canUseFollowButton && (
                      <span
                        className="shrink-0 rounded-full border border-zinc-200 bg-zinc-50 px-2.5 py-1 text-xs font-medium text-zinc-400"
                        data-testid={`member-follow-unavailable-${member.id}`}
                      >
                        Unavailable
                      </span>
                    )}
                    {canUseFollowButton && (
                      <PlayerFollowButton
                        userId={member.id}
                        username={member.display_name || member.username || 'member'}
                        isFollowing={followState === 'following'}
                        isRequested={followState === 'requested'}
                        onFollowChange={handleMemberFollowChange}
                        size="sm"
                        className="shrink-0"
                      />
                    )}
                  </li>
                );
              })}
            </ul>
            {club.member_count > club.members.length && (
              <p className="text-xs text-zinc-500 mt-3 text-center" data-testid="member-list-overflow">
                + {club.member_count - club.members.length} more member{club.member_count - club.members.length === 1 ? '' : 's'}
              </p>
            )}
          </div>
        )}
        
        {/* Upcoming Runs */}
        <div>
          <h2 className="text-sm font-semibold text-zinc-500 uppercase tracking-wide mb-4">
            Upcoming Runs
          </h2>
          
          {club?.upcoming_runs && club.upcoming_runs.length > 0 ? (
            <div className="space-y-3" data-testid="upcoming-runs">
              {club.upcoming_runs.map((run) => (
                <Card
                  key={run.id}
                  className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/games/${run.id}`)}
                  data-testid={`run-card-${run.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-zinc-900">{run.title}</h3>
                      <div className="flex items-center gap-2 text-sm text-zinc-500 mt-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatRunDate(run.date_time)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-zinc-500 mt-1">
                        <MapPin className="w-4 h-4" />
                        <span>{run.location_name}</span>
                      </div>
                      {Array.isArray(run.run_club_ids) && run.run_club_ids.filter(id => id !== club.id).length > 0 && (
                        <p className="text-xs text-zinc-500 mt-2">
                          Also posted to {run.run_club_ids.filter(id => id !== club.id).length} other club{run.run_club_ids.filter(id => id !== club.id).length === 1 ? '' : 's'}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-zinc-900">
                        {run.players_joined}/{run.max_players}
                      </div>
                      <div className="text-xs text-zinc-500">players</div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-8 text-center bg-zinc-100/50 border-dashed" data-testid="no-runs-state">
              <div className="text-4xl mb-3">🏀</div>
              <p className="text-zinc-600 font-medium">
                No upcoming club runs yet
              </p>
              <p className="text-zinc-500 text-sm mt-1">
                Check back soon for scheduled runs.
              </p>
            </Card>
          )}
        </div>

        {/* Past Runs — small section, read-only, only renders when data exists */}
        {club?.past_runs && club.past_runs.length > 0 && (
          <div className="mt-8" data-testid="past-runs-section">
            <h2 className="text-sm font-semibold text-zinc-500 uppercase tracking-wide mb-4">
              Recent Runs
            </h2>
            <div className="space-y-2">
              {club.past_runs.map((run) => (
                <button
                  type="button"
                  key={run.id}
                  onClick={() => navigate(`/games/${run.id}`)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 transition-colors text-left active:scale-[0.99]"
                  data-testid={`past-run-card-${run.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-zinc-900 truncate">{run.title}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {formatRunDate(run.date_time)}
                      {run.status === 'cancelled' && <span className="ml-2 text-red-500">Cancelled</span>}
                    </p>
                    {Array.isArray(run.run_club_ids) && run.run_club_ids.filter(id => id !== club.id).length > 0 && (
                      <p className="text-xs text-zinc-500 mt-0.5">
                        Also posted to {run.run_club_ids.filter(id => id !== club.id).length} other club{run.run_club_ids.filter(id => id !== club.id).length === 1 ? '' : 's'}
                      </p>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 shrink-0">
                    {run.players_joined}/{run.max_players}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Delete Group — host only */}
        {isOwner && (
          <div className="mt-12 pt-8 border-t border-zinc-200">
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 text-sm text-red-500 hover:text-red-600 transition-colors"
              data-testid="delete-club-btn"
            >
              <Trash2 className="w-4 h-4" />
              Delete Group
            </button>
          </div>
        )}

        {/* Leave Group — members only, not host */}
        {isMember && !isOwner && (
          <div className="mt-12 pt-8 border-t border-zinc-200">
            <button
              onClick={() => setShowLeaveConfirm(true)}
              className="flex items-center gap-2 text-sm text-zinc-500 hover:text-red-500 transition-colors"
              data-testid="leave-club-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Leave Group
            </button>
          </div>
        )}
      </div>

      {/* Leave Confirmation Modal */}
      {showLeaveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="leave-club-modal">
          <Card className="max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-zinc-900 mb-2">Leave this group?</h3>
            <p className="text-sm text-zinc-600 mb-6">You can rejoin anytime.</p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowLeaveConfirm(false)}
                disabled={leaving}
                data-testid="leave-club-cancel"
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={handleLeaveClub}
                disabled={leaving}
                data-testid="leave-club-confirm"
              >
                {leaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Leave'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="delete-club-modal">
          <Card className="max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-zinc-900 mb-2">Delete this group?</h3>
            <p className="text-sm text-zinc-600 mb-6">This cannot be undone.</p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                data-testid="delete-club-cancel"
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={handleDeleteClub}
                disabled={deleting}
                data-testid="delete-club-confirm"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
              </Button>
            </div>
          </Card>
        </div>
      )}
      {/* Manage Members Sheet — host-only add/remove of adult members */}
      {canManageMembers && club && (
        <ManageMembersSheet
          open={showManageMembers}
          onOpenChange={setShowManageMembers}
          clubId={club.id}
          clubName={club.name}
          token={token}
          members={club.members || []}
          totalMemberCount={club.member_count}
          currentUserId={user?.id}
          ownerUserId={club.host_user_id}
          canManageRoles={canManageRoles}
          onChanged={refreshClub}
        />
      )}
    </div>
  );
};

export default ClubDetail;
